// ============================================================================
// Estimate context — the commercial half of the order (client, addresses, quoted
// line items), shown alongside the OMS fulfilment view. The SO is 1:1 with the estimate.
// ============================================================================
import { EnvironmentOutlined } from "@ant-design/icons";
import { Card, Col, Descriptions, Row, Tag, Typography } from "antd";
import React from "react";
import { Link } from "react-router-dom";
import { EstimateAddress, EstimateContext } from "../types";
import { googleMapsUrl } from "../format";
import CopyText from "./copy_text";
import DetailSection from "./detail_section";

// The client name as a link into the client's own screen — the same /client/:id page the Clients
// list opens, in a new tab so the order stays put behind it. Plain text when the estimate carries
// no client id (an unlinked estimate), so the row never renders a dead link.
const ClientLink: React.FC<{ id?: string | null; name?: string | null }> = ({ id, name }) => {
  if (!name) return <>—</>;
  if (!id) return <>{name}</>;
  return (
    <Link to={`/client/${id}`} target="_blank" rel="noopener noreferrer">
      {name}
    </Link>
  );
};

// The GST identity of the order, shown on Bill To.
//
// TWO systems answer "is this B2B?" and they do NOT agree, which is the whole reason this block
// exists rather than a single GSTIN line:
//   * OMS  (`resolvers.customer_gstin`)                    — bill_address.gst_no OR estimate.client_gst
//   * legacy (`DocumentRepository.get_zoho_purchase_customer_id`) — estimate.client_gst ALONE
// OMS writes the SO / invoice / DC and runs the pre-ship payment gate; legacy writes the
// capture-time customer payment. So when the billing address carries a GSTIN and `client_gst` is
// blank, OMS invoices a B2B contact while legacy books the money onto the B2C one — the gate
// searches payments scoped to the B2B contact, finds nothing, and the leg holds forever on
// "not fully paid in Zoho" with the customer's money sitting unused one contact over.
//
// `resolved` is the OMS answer (what the documents and the gate do), so the B2B/B2C tag reflects
// what Zoho will actually bill. The split is called out loudly because it PREDICTS that hold —
// the repair is Actions › Reconcile › "Move payment to the right contact".
const GstIdentity: React.FC<{
  clientGst?: string | null;
  resolved?: string | null;
}> = ({ clientGst, resolved }) => {
  const gst = (resolved || "").trim();
  const onEstimate = (clientGst || "").trim();
  // Legacy books the payment B2C whenever `client_gst` is blank. Paired with a GSTIN-bearing
  // billing address, that is the misplaced-payment setup.
  const willSplit = !!gst && !onEstimate;

  return (
    <div style={{ marginTop: 8 }}>
      <Tag color={gst ? "blue" : "default"} style={{ marginRight: 6 }}>
        {gst ? "B2B" : "B2C"}
      </Tag>
      {gst ? (
        <CopyText value={gst} label="GSTIN">
          <Typography.Text code>{gst}</Typography.Text>
        </CopyText>
      ) : (
        <Typography.Text type="secondary">No GSTIN</Typography.Text>
      )}

      {willSplit && (
        <div style={{ marginTop: 4, color: "#d46b08" }}>
          <Tag color="orange" style={{ marginRight: 6 }}>
            payment contact split
          </Tag>
          B2B from the address only — the estimate has no GSTIN, so the capture-time push books the
          payment to this customer&rsquo;s B2C contact while invoices go to the B2B one. Expect an
          invoice-error hold; fix with Actions &rsaquo; Reconcile &rsaquo; Move payment to the right
          contact.
        </div>
      )}
    </div>
  );
};

// `showMap` adds a Google Maps link — pinned on the address's real lat/long when we have it, else a
// text search. Used for the Ship To block so ops can jump straight to the customer's location.
// `gst` is the Bill To block only: shipping does not decide the customer's tax identity.
const AddressBlock: React.FC<{
  title: string;
  addr: EstimateAddress | null;
  showMap?: boolean;
  gst?: React.ReactNode;
}> = ({ title, addr, showMap, gst }) => {
  const mapUrl =
    showMap && addr
      ? googleMapsUrl(addr, [addr.flat_no, addr.address, addr.city, addr.state, addr.pincode])
      : null;
  return (
    <Card size="small" title={title} style={{ height: "100%" }}>
      {addr ? (
        <div style={{ fontSize: 13, lineHeight: 1.6 }}>
          {addr.name && <div><strong>{addr.name}</strong></div>}
          <div>
            {[addr.flat_no, addr.address].filter(Boolean).join(", ")}
          </div>
          <div>{[addr.city, addr.state, addr.pincode].filter(Boolean).join(" ")}</div>
          {(addr.contact_person_name || addr.contact_person_contact) && (
            <div style={{ marginTop: 4, color: "#666" }}>
              {addr.contact_person_name} {addr.contact_person_contact}
            </div>
          )}
          {mapUrl && (
            <div style={{ marginTop: 6 }}>
              <a href={mapUrl} target="_blank" rel="noopener noreferrer">
                <EnvironmentOutlined /> View on Google Maps
              </a>
            </div>
          )}
          {gst}
        </div>
      ) : (
        // The GST identity is a property of the ORDER, not of the address row, so it still renders
        // when no billing address is attached — that combination is exactly when someone needs it.
        <div style={{ fontSize: 13, lineHeight: 1.6 }}>
          <span style={{ color: "#aaa" }}>—</span>
          {gst}
        </div>
      )}
    </Card>
  );
};


const EstimateContextView: React.FC<{ estimate: EstimateContext }> = ({ estimate }) => (
  <DetailSection
    sectionKey="estimate"
    title="Order / Estimate"
    summary={
      <Tag bordered={false}>{estimate.client_name || estimate.lead_id || `Estimate ${estimate.id}`}</Tag>
    }
  >
    <Descriptions size="small" column={3} bordered style={{ marginBottom: 16 }}>
      <Descriptions.Item label="Lead ID">
        <CopyText value={estimate.lead_id} label="Lead ID">
          <strong>{estimate.lead_id || "—"}</strong>
        </CopyText>
      </Descriptions.Item>
      <Descriptions.Item label="Order Status">
        {estimate.estimate_status ? estimate.estimate_status.replace(/_/g, " ") : "—"}
      </Descriptions.Item>
      <Descriptions.Item label="Source">{estimate.order_source || "—"}</Descriptions.Item>
      <Descriptions.Item label="Client">
        <ClientLink id={estimate.client_id} name={estimate.client_name} />
      </Descriptions.Item>
      <Descriptions.Item label="Client Contact">{estimate.client_contact || "—"}</Descriptions.Item>
      <Descriptions.Item label="Total">
        {estimate.total_estimate_amount != null
          ? `₹${Number(estimate.total_estimate_amount).toFixed(2)}`
          : "—"}
      </Descriptions.Item>
      <Descriptions.Item label="Created by">{estimate.created_by || "—"}</Descriptions.Item>
      <Descriptions.Item label="Assigned to">{estimate.assigned_to || "—"}</Descriptions.Item>
      <Descriptions.Item label="Estimate #">{estimate.id}</Descriptions.Item>
    </Descriptions>

    <Row gutter={16} style={{ marginBottom: 16 }}>
      <Col xs={24} md={12}>
        <AddressBlock
          title="Bill To"
          addr={estimate.bill_address}
          gst={
            <GstIdentity
              clientGst={estimate.client_gst}
              resolved={estimate.customer_gstin}
            />
          }
        />
      </Col>
      <Col xs={24} md={12}>
        <AddressBlock title="Ship To" addr={estimate.ship_address} showMap />
      </Col>
    </Row>
  </DetailSection>
);

export default EstimateContextView;
